/**
 * @fileoverview FeatureModule for loading modules based on browser feature detection.
 * 
 * This loader handles modules that should only be loaded when specific browser
 * features or capabilities are available, enabling progressive enhancement
 * and feature-specific functionality.
 * 
 * @author Roshan Jose
 * @version 2.0.0
 * @since 2025
 */

import { RouterHelper } from "../router-helper";
import { IConditionalLoader, ModuleLoaderStatus } from "../interfaces/module-loader.interfaces";

/**
 * FeatureModule manages conditional loading based on browser capabilities.
 * 
 * This loader is responsible for:
 * - Detecting browser features and capabilities
 * - Loading modules that depend on specific browser features
 * - Providing progressive enhancement capabilities
 * - Supporting modern web APIs conditionally
 * 
 * The FeatureModule follows the singleton pattern and implements the IConditionalLoader
 * interface for consistent behavior across the router system.
 * 
 * Potential Features to Detect:
 * - Service Worker support
 * - Web Workers support
 * - IndexedDB availability
 * - WebGL capabilities
 * - Touch support
 * - Geolocation API
 * - Push notifications
 * 
 * Features:
 * - Singleton pattern for global access
 * - Browser feature detection
 * - Conditional module loading based on capabilities
 * - Graceful degradation for unsupported features
 * - Comprehensive error handling and logging
 * 
 * @class FeatureModule
 * @implements {IConditionalLoader}
 */
export class FeatureModule implements IConditionalLoader {
    /** Singleton instance of the FeatureModule */
    private static instance: FeatureModule;
    
    /** RouterHelper instance for secure module loading operations */
    private readonly routerHelper: RouterHelper = RouterHelper.getInstance();
    
    /** Current status and state of the FeatureModule */
    private readonly status: ModuleLoaderStatus = {
        isInitialized: false,
        loadedModules: [],
        failedModules: [],
        isLoading: false
    };
    
    /**
     * Gets the singleton instance of FeatureModule.
     * Creates a new instance if one doesn't exist.
     * @returns The singleton FeatureModule instance
     */
    static getInstance(): FeatureModule {
        this.instance ??= new FeatureModule();
        return this.instance;
    }    
   
    /**
     * Initializes the FeatureModule.
     * 
     * Sets up the loader for feature-based module loading operations.
     * This is a lightweight initialization as feature detection happens
     * during the actual loading phase.
     * 
     * @returns Promise that resolves when initialization is complete
     * @throws {Error} When initialization fails
     */
    async init(): Promise<void> {
        try {
            this.status.isInitialized = true;
        } catch (error) {
            this.status.lastError = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    /**
     * Returns the human-readable name of this loader.
     * Used for logging, debugging, and status reporting.
     * @returns The loader's display name
     */
    getName(): string {
        return 'FeatureModule';
    }

    /**
     * Returns a copy of the current loader status.
     * Provides real-time information about the loader's state.
     * @returns Current loader status information
     */
    getStatus(): ModuleLoaderStatus {
        return { ...this.status };
    }

    /**
     * Loads all feature-based modules (delegates to loadConditionalFeatureModules).
     * 
     * This is the main entry point for loading feature modules,
     * implementing the IModuleLoader interface contract.
     * 
     * @returns Promise that resolves when all feature modules are loaded
     */
    async loadModules(): Promise<void> {
        return this.loadConditionalFeatureModules();
    }

    /**
     * Placeholder for page-specific conditional loading.
     * 
     * FeatureModule focuses on browser capabilities rather than page-specific
     * conditions, so this method logs that no page modules are handled.
     * Required by the IConditionalLoader interface.
     * 
     * @returns Promise that resolves immediately
     */
    async loadConditionalPageModules(): Promise<void> {
        // FeatureModule doesn't handle page modules, but interface requires it
        this.routerHelper.routerConsoleLog('FeatureModule does not handle page modules', false, 'FeatureModule');
    }

    /**
     * Loads modules based on browser feature detection and capabilities.
     * 
     * Detects available browser features and conditionally loads modules
     * that provide enhanced functionality for supported features.
     * 
     * Currently includes commented examples for:
     * - Service Worker support detection and loading
     * 
     * Additional features can be easily added with new detection blocks:
     * - Web Workers, IndexedDB, WebGL, Touch, Geolocation, etc.
     * 
     * @returns Promise that resolves when feature modules are loaded
     * @throws {Error} When feature module loading fails
     */
    async loadConditionalFeatureModules(): Promise<void> {
        this.status.isLoading = true;
        
        try {
            // Load modules based on browser capabilities
            // if ('serviceWorker' in navigator) {
            //     await this.routerHelper.loadModuleSafely(
            //         'service-worker',
            //         () => import(/* webpackChunkName: "service-worker-loader" */ '../serviceWorker'),
            //         'Service Worker'
            //     );
            //     this.status.loadedModules.push('service-worker');
            // }
            
            this.routerHelper.routerConsoleLog('Feature modules loading completed (no modules to load)', false, 'FeatureModule');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this.status.failedModules.push('feature-modules');
            this.status.lastError = errorMessage;
            this.routerHelper.routerConsoleLog(`Failed to load conditional feature modules - ${errorMessage}`, true, 'FeatureModule');
            throw error;
        } finally {
            this.status.isLoading = false;
        }
    }
}
